import { Note } from '@/@types/note';
import { queryClient } from '@/services/queryClient';
import { api } from '@/services/apiClient';
import { useMutation } from '@tanstack/react-query';

import { NoteFormData } from '../schemas/note-form-schema';
import { getNote } from './useGetNote';

type UpdateNoteProps = NoteFormData & {
  id: number;
};

async function createNote(payload: NoteFormData): Promise<Note> {
  const { data } = await api.post<Note>('/notes', payload);

  return data;
}

async function updateNote({ id, ...payload }: UpdateNoteProps): Promise<Note> {
  const { data } = await api.put<Note>('/notes', { id, ...payload });

  return data;
}

async function deleteNote(id: number): Promise<void> {
  await api.delete(`/notes/${id}`);
}

async function restoreNote(id: number): Promise<Note> {
  const { data } = await api.patch<Note>(`/notes/${id}`);

  return data;
}

export function useNote() {
  const createNoteMutation = useMutation({
    mutationFn: createNote,
    onSuccess: () => queryClient.invalidateQueries(['notes']),
  });
  const updateNoteMutation = useMutation({
    mutationFn: updateNote,
    onSuccess: (note) => {
      queryClient.invalidateQueries(['notes']);
      queryClient.invalidateQueries(['note', note.id]);
    },
  });
  const deleteNoteMutation = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => queryClient.invalidateQueries(['notes']),
  });
  const restoreNoteMutation = useMutation({
    mutationFn: restoreNote,
    onSuccess: (note) => {
      queryClient.invalidateQueries(['notes']);
      queryClient.invalidateQueries(['note', note.id]);
    },
  });

  async function prefetchNote(noteId: number) {
    await queryClient.prefetchQuery(
      ['note', noteId],
      () => getNote({ id: noteId }),
      { staleTime: 1000 * 60 }
    );
  }

  return {
    createNoteMutation,
    updateNoteMutation,
    deleteNoteMutation,
    restoreNoteMutation,
    prefetchNote,
  };
}
